import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BomberosHeader } from '../../components/BomberosHeader';
import { 
    bomberosKPIAPI, 
    bomberosCompaniaAPI, 
    bomberosPersonalAPI,
    bomberosMaterialMayorAPI,
    bomberosMaterialMenorAPI,
    bomberosCapacitacionAPI
} from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Download,
    FileText,
    BarChart3,
    Users,
    Truck,
    HardHat,
    GraduationCap,
    Calendar,
    TrendingUp,
    TrendingDown,
    Minus,
    Building2,
    Printer,
    RefreshCw,
} from 'lucide-react';

export default function BomberosReportesPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const reportRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [periodo, setPeriodo] = useState('anual');
    const [companiaFilter, setCompaniaFilter] = useState('all');
    const [companias, setCompanias] = useState([]);
    const [reportData, setReportData] = useState({
        kpis: null,
        bomberos: [],
        vehiculos: [],
        epp: [],
        capacitaciones: [],
    });

    useEffect(() => {
        loadCompanias();
    }, []);

    useEffect(() => {
        loadReportData();
    }, [periodo, companiaFilter]);

    const loadCompanias = async () => {
        try {
            const res = await bomberosCompaniaAPI.getAll();
            setCompanias(res.data);
        } catch (err) {
            console.error('Error loading companias:', err);
        }
    };

    const loadReportData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (companiaFilter !== 'all') {
                params.compania_id = companiaFilter;
            }

            const [kpisRes, bomberosRes, vehiculosRes, eppRes, capsRes] = await Promise.all([
                bomberosKPIAPI.get(params),
                bomberosPersonalAPI.getAll(companiaFilter !== 'all' ? { compania_id: companiaFilter } : {}),
                bomberosMaterialMayorAPI.getAll(companiaFilter !== 'all' ? { compania_id: companiaFilter } : {}),
                bomberosMaterialMenorAPI.getAll(companiaFilter !== 'all' ? { compania_id: companiaFilter } : {}),
                bomberosCapacitacionAPI.getAll(),
            ]);

            setReportData({
                kpis: kpisRes.data,
                bomberos: bomberosRes.data,
                vehiculos: [...(vehiculosRes.data || [])].sort((a, b) => (a.compania_nombre || '').localeCompare(b.compania_nombre || '')),
                epp: eppRes.data,
                capacitaciones: capsRes.data,
            });
        } catch (err) {
            console.error('Error loading report data:', err);
            toast.error('Error al cargar datos del reporte');
        } finally {
            setLoading(false);
        }
    };

    const getPeriodoLabel = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const quarter = Math.floor(month / 3) + 1;

        switch (periodo) {
            case 'mensual':
                return `${now.toLocaleString('es', { month: 'long' })} ${year}`;
            case 'trimestral':
                return `Q${quarter} ${year}`;
            case 'semestral':
                return month < 6 ? `1er Semestre ${year}` : `2do Semestre ${year}`;
            case 'anual':
                return `Año ${year}`;
            default:
                return year.toString();
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExportPDF = async () => {
        setGeneratingPDF(true);
        toast.info('Preparando PDF...');
        
        // Use browser print to PDF
        setTimeout(() => {
            window.print();
            setGeneratingPDF(false);
        }, 500);
    };

    const getStatusIcon = (value, threshold = 80) => {
        if (value >= threshold) return <TrendingUp className="h-4 w-4 text-green-500" />;
        if (value >= threshold * 0.6) return <Minus className="h-4 w-4 text-yellow-500" />;
        return <TrendingDown className="h-4 w-4 text-red-500" />;
    };

    // Calculate additional stats
    const stats = {
        bomberosActivos: reportData.bomberos.filter(b => b.activo).length,
        bomberosInactivos: reportData.bomberos.filter(b => !b.activo).length,
        vehiculosOperativos: reportData.vehiculos.filter(v => v.estado_operativo === 'operativo').length,
        vehiculosMantenimiento: reportData.vehiculos.filter(v => v.estado_operativo === 'mantenimiento').length,
        vehiculosFueraServicio: reportData.vehiculos.filter(v => v.estado_operativo === 'fuera_servicio').length,
        eppDisponible: reportData.epp.filter(e => e.estado === 'disponible').length,
        eppAsignado: reportData.epp.filter(e => e.estado === 'asignado').length,
        eppMantenimiento: reportData.epp.filter(e => e.estado === 'mantenimiento').length,
        capacitacionesProgramadas: reportData.capacitaciones.filter(c => c.estado === 'programada').length,
        capacitacionesEnCurso: reportData.capacitaciones.filter(c => c.estado === 'en_curso').length,
        capacitacionesFinalizadas: reportData.capacitaciones.filter(c => c.estado === 'finalizada').length,
    };

    // Group bomberos by grado
    const bomberosPorGrado = reportData.bomberos.reduce((acc, b) => {
        if (b.activo) {
            acc[b.grado] = (acc[b.grado] || 0) + 1;
        }
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header - No print */}
            <div className="flex items-center justify-between print:hidden">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <BarChart3 className="h-6 w-6" />
                            Reportes de Gestión
                        </h1>
                        <p className="text-muted-foreground">Informes de KPIs y cumplimiento institucional</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={loadReportData}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                    <Button variant="outline" onClick={handlePrint}>
                        <Printer className="h-4 w-4 mr-2" />
                        Imprimir
                    </Button>
                    {tienePermiso('exportar_reportes') && (
                        <Button onClick={handleExportPDF} disabled={generatingPDF}>
                            <Download className="h-4 w-4 mr-2" />
                            Exportar PDF
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters - No print */}
            <Card className="border-2 print:hidden">
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <label className="text-sm font-medium mb-2 block">Período del Reporte</label>
                            <Select value={periodo} onValueChange={setPeriodo}>
                                <SelectTrigger data-testid="select-periodo">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="mensual">Mensual</SelectItem>
                                    <SelectItem value="trimestral">Trimestral</SelectItem>
                                    <SelectItem value="semestral">Semestral</SelectItem>
                                    <SelectItem value="anual">Anual</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="text-sm font-medium mb-2 block">Compañía</label>
                            <Select value={companiaFilter} onValueChange={setCompaniaFilter}>
                                <SelectTrigger data-testid="select-compania">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas las Compañías</SelectItem>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Printable Report */}
            <div ref={reportRef} className="print:p-0">
                {/* Report Header */}
                <Card className="border-2 mb-6">
                    <CardHeader className="text-center border-b">
                        <div className="flex items-center justify-center gap-3 mb-2">
                            <Building2 className="h-8 w-8 text-primary" />
                            <div>
                                <CardTitle className="text-xl">CUERPO DE BOMBEROS DE SALAMANCA</CardTitle>
                                <CardDescription>Informe de Gestión - {getPeriodoLabel()}</CardDescription>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Generado el {new Date().toLocaleDateString('es-CL', { 
                                day: 'numeric', 
                                month: 'long', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </p>
                    </CardHeader>
                </Card>

                {/* KPIs Summary */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <BarChart3 className="h-5 w-5" />
                            Resumen de KPIs Institucionales
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center p-4 rounded-lg bg-muted/50">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <GraduationCap className="h-5 w-5 text-purple-500" />
                                    {getStatusIcon(reportData.kpis?.porcentaje_academico || 0)}
                                </div>
                                <p className="text-3xl font-bold text-purple-600">
                                    {reportData.kpis?.porcentaje_academico || 0}%
                                </p>
                                <p className="text-sm text-muted-foreground">Cumplimiento Académico</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {reportData.kpis?.cursos_realizados || 0} de {reportData.kpis?.cursos_programados || 0} cursos
                                </p>
                            </div>

                            <div className="text-center p-4 rounded-lg bg-muted/50">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Truck className="h-5 w-5 text-green-500" />
                                    {getStatusIcon(reportData.kpis?.porcentaje_operatividad_mayor || 0)}
                                </div>
                                <p className="text-3xl font-bold text-green-600">
                                    {reportData.kpis?.porcentaje_operatividad_mayor || 0}%
                                </p>
                                <p className="text-sm text-muted-foreground">Operatividad Material Mayor</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {reportData.kpis?.material_mayor_operativo || 0} de {reportData.kpis?.material_mayor_total || 0} unidades
                                </p>
                            </div>

                            <div className="text-center p-4 rounded-lg bg-muted/50">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <HardHat className="h-5 w-5 text-orange-500" />
                                    {getStatusIcon(reportData.kpis?.porcentaje_operatividad_menor || 0)}
                                </div>
                                <p className="text-3xl font-bold text-orange-600">
                                    {reportData.kpis?.porcentaje_operatividad_menor || 0}%
                                </p>
                                <p className="text-sm text-muted-foreground">Operatividad EPP</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {reportData.kpis?.material_menor_disponible || 0} de {reportData.kpis?.material_menor_total || 0} equipos
                                </p>
                            </div>

                            <div className="text-center p-4 rounded-lg bg-muted/50">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Users className="h-5 w-5 text-blue-500" />
                                    {getStatusIcon(reportData.kpis?.promedio_asistencia || 0, 70)}
                                </div>
                                <p className="text-3xl font-bold text-blue-600">
                                    {reportData.kpis?.promedio_asistencia || 0}%
                                </p>
                                <p className="text-sm text-muted-foreground">Asistencia Promedio</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {reportData.kpis?.actividades_totales || 0} actividades registradas
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Personal Section */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Dotación de Personal
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="font-medium mb-4">Estado General</h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span>Personal Activo</span>
                                        <Badge variant="outline" className="text-green-600">{stats.bomberosActivos}</Badge>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span>Personal Inactivo (Bajas)</span>
                                        <Badge variant="outline" className="text-red-600">{stats.bomberosInactivos}</Badge>
                                    </div>
                                    <Separator />
                                    <div className="flex justify-between items-center font-medium">
                                        <span>Total Registrados</span>
                                        <Badge>{reportData.bomberos.length}</Badge>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-medium mb-4">Distribución por Grado</h4>
                                <div className="space-y-2">
                                    {['comandante', 'capitan', 'teniente', 'bombero', 'voluntario'].map(grado => (
                                        <div key={grado} className="flex items-center gap-2">
                                            <span className="w-24 text-sm capitalize">{grado}:</span>
                                            <Progress 
                                                value={((bomberosPorGrado[grado] || 0) / stats.bomberosActivos) * 100} 
                                                className="flex-1 h-2"
                                            />
                                            <span className="w-8 text-right text-sm font-medium">
                                                {bomberosPorGrado[grado] || 0}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Material Mayor Section */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Truck className="h-5 w-5" />
                            Material Mayor (Vehículos)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="text-center p-3 rounded-lg bg-green-50 dark:bg-green-950">
                                <p className="text-2xl font-bold text-green-600">{stats.vehiculosOperativos}</p>
                                <p className="text-xs text-muted-foreground">Operativos</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950">
                                <p className="text-2xl font-bold text-yellow-600">{stats.vehiculosMantenimiento}</p>
                                <p className="text-xs text-muted-foreground">En Mantenimiento</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-red-50 dark:bg-red-950">
                                <p className="text-2xl font-bold text-red-600">{stats.vehiculosFueraServicio}</p>
                                <p className="text-xs text-muted-foreground">Fuera de Servicio</p>
                            </div>
                        </div>

                        {reportData.vehiculos.length > 0 && (
                            <div className="mt-4">
                                <h4 className="font-medium mb-2 text-sm">Detalle de Unidades</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                    {reportData.vehiculos.map(v => (
                                        <div key={v.id} className="flex items-center justify-between p-2 rounded bg-muted/50 gap-2">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="truncate">{v.codigo} - {v.marca} {v.modelo}</span>
                                                {v.compania_nombre && (
                                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 whitespace-nowrap shrink-0">
                                                        {v.compania_nombre.includes('Primera') ? '1a Cía' :
                                                         v.compania_nombre.includes('Segunda') ? '2a Cía' :
                                                         v.compania_nombre.includes('Tercera') ? '3a Cía' :
                                                         v.compania_nombre.includes('Cuarta') ? '4a Cía' :
                                                         v.compania_nombre.includes('Quinta') ? '5a Cía' :
                                                         v.compania_nombre.includes('Comandancia') ? 'Cmd' :
                                                         v.compania_nombre.split(' - ')[0].split(' ').slice(0, 2).join(' ')}
                                                    </Badge>
                                                )}
                                            </div>
                                            <Badge variant="outline" className={
                                                v.estado_operativo === 'operativo' ? 'text-green-600 shrink-0' :
                                                v.estado_operativo === 'mantenimiento' ? 'text-yellow-600 shrink-0' : 'text-red-600 shrink-0'
                                            }>
                                                {v.estado_operativo}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* EPP Section */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <HardHat className="h-5 w-5" />
                            Material Menor y EPP
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-4 gap-4">
                            <div className="text-center p-3 rounded-lg bg-muted/50">
                                <p className="text-2xl font-bold">{reportData.epp.length}</p>
                                <p className="text-xs text-muted-foreground">Total Inventario</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-green-50 dark:bg-green-950">
                                <p className="text-2xl font-bold text-green-600">{stats.eppDisponible}</p>
                                <p className="text-xs text-muted-foreground">Disponible</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-blue-50 dark:bg-blue-950">
                                <p className="text-2xl font-bold text-blue-600">{stats.eppAsignado}</p>
                                <p className="text-xs text-muted-foreground">Asignado</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950">
                                <p className="text-2xl font-bold text-yellow-600">{stats.eppMantenimiento}</p>
                                <p className="text-xs text-muted-foreground">Mantenimiento</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Capacitaciones Section */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <GraduationCap className="h-5 w-5" />
                            Estado de Capacitaciones
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 rounded-lg bg-blue-50 dark:bg-blue-950">
                                <p className="text-2xl font-bold text-blue-600">{stats.capacitacionesProgramadas}</p>
                                <p className="text-xs text-muted-foreground">Programadas</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-green-50 dark:bg-green-950">
                                <p className="text-2xl font-bold text-green-600">{stats.capacitacionesEnCurso}</p>
                                <p className="text-xs text-muted-foreground">En Curso</p>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                                <p className="text-2xl font-bold text-gray-600">{stats.capacitacionesFinalizadas}</p>
                                <p className="text-xs text-muted-foreground">Finalizadas</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Footer */}
                <div className="text-center text-xs text-muted-foreground border-t pt-4">
                    <p>Documento generado automáticamente por Sistema de Gestión de Bomberos</p>
                    <p>Este reporte es válido solo como información de gestión interna</p>
                </div>
            </div>

            {/* Print Styles */}
            <style>{`
                @media print {
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .print\\:hidden { display: none !important; }
                    .print\\:p-0 { padding: 0 !important; }
                }
            `}</style>
        </div>
        </>
    );
}
